import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { createPlatformTheme, type ColorMode } from './createPlatformTheme';

export type ColorModePreference = ColorMode | 'system';

interface ColorModeContextValue {
  /** What the user chose — may be 'system'. */
  preference: ColorModePreference;
  /** What is actually rendered right now. */
  mode: ColorMode;
  setPreference: (preference: ColorModePreference) => void;
  toggle: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

export function useColorMode(): ColorModeContextValue {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error('useColorMode must be used inside <PlatformThemeProvider>.');
  }
  return context;
}

export interface PlatformThemeProviderProps {
  children: ReactNode;
  /** Overrides the stored/system preference. Useful for the component gallery. */
  forceMode?: ColorMode;
}

/**
 * The single theme boundary for the whole platform.
 *
 * LIGHT ONLY (owner's ruling, 2026-08-02). The product ships one look.
 *
 * Dark mode was here and is deliberately gone: the toggle, the system-preference
 * follow, and the stored preference. The ported APR screens carry hundreds of
 * hard-coded hex colours in inline styles and in `source-css/app.css` — a
 * byte-for-byte copy of the source stylesheet that must not be edited — so dark
 * mode only ever themed the shell around them. Half a dark product is worse than
 * none, and it was costing a second visual state to check on every screen for a
 * result nobody could ship.
 *
 * `useColorMode` is kept, always answering 'light', so the handful of callers do
 * not have to be rewritten and reinstating dark is a change here rather than a
 * hunt through the app. A stored preference from before is ignored, not migrated.
 */
export function PlatformThemeProvider({ children, forceMode }: PlatformThemeProviderProps) {
  // `forceMode` still honoured — the component gallery uses it to render a dark
  // sample deliberately. Nothing in the product passes it.
  const mode: ColorMode = forceMode ?? 'light';
  const preference: ColorModePreference = 'light';

  // Kept so callers still type-check; both are no-ops while the product is light only.
  const setPreference = useCallback((_next: ColorModePreference) => {}, []);
  const toggle = useCallback(() => {}, []);

  useEffect(() => {
    // Lets non-MUI surfaces (and the browser's own form controls) follow the theme.
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = mode;
      document.documentElement.style.colorScheme = mode;
    }
  }, [mode]);

  const theme = useMemo(() => createPlatformTheme(mode), [mode]);
  const contextValue = useMemo<ColorModeContextValue>(
    () => ({ preference, mode, setPreference, toggle }),
    [preference, mode, setPreference, toggle],
  );

  return (
    <ColorModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
